// DEPLOY-PROBE-bump

import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { HomePage } from './layouts/HomePage/HomePage';
import { Login } from './components/Login/Login';
import { EmployeeView } from './components/EmployeeView/EmployeeView';
import { ConstructionWorkerView } from './components/ConstructionWorkerView/ConstructionWorkerView';
import { ScrollToTop } from './components/ScrollToTop';
import EmployeeService from './services/EmployeeService';

/**
 * Main App Component
 * 
 * This component serves as the root component of the payslip management system.
 * It handles authentication state management and renders either the login page
 * or the main application based on user authentication status.
 * 
 * Features:
 * - Authentication state management using React hooks
 * - Session-scoped login using sessionStorage (cleared when tab closes)
 * - Conditional rendering of Login vs HomePage components
 * - Logout functionality with state cleanup
 */
export const App = () => {
  // Authentication state - tracks whether user is logged in
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Current employee data
  const [currentEmployee, setCurrentEmployee] = useState<any>(null);

  /**
   * Effect hook to check for existing authentication on component mount
   * 
   * Uses sessionStorage so login persists on refresh but is cleared when the tab is closed.
   */
  useEffect(() => {
    const savedEmployee = sessionStorage.getItem('currentEmployee');
    
    if (savedEmployee) {
      try {
        const employee = JSON.parse(savedEmployee);
        setCurrentEmployee(employee);
        setIsAuthenticated(true);
      } catch (error) {
        sessionStorage.removeItem('currentEmployee');
      }
    }
  }, []);

  /**
   * Handles successful user login
   * 
   * @param employee - The authenticated employee data
   * 
   * This function:
   * - Updates the authentication state
   * - Stores the employee data
   * - Persists authentication data to sessionStorage (cleared when tab closes)
   */
  const handleLogin = (employee: any) => {
    setCurrentEmployee(employee);
    setIsAuthenticated(true);
    // Employee data is already stored in localStorage by the Login component
  };

  /**
   * Handles user logout
   * 
   * This function:
   * - Clears the authentication state
   * - Removes the employee data
   * - Cleans up sessionStorage to prevent automatic re-login
   * - Calls the service logout method to clear JWT token
   */
  const handleLogout = () => {
    setIsAuthenticated(false);
    setCurrentEmployee(null);
    // Use service logout method to clear all authentication data
    EmployeeService.logout();
  };

  // Debug logging
  
  // Add scroll reset effect for route changes
  useEffect(() => {
    const handleRouteChange = () => {
      // Reset scroll position immediately
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', handleRouteChange);
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);
  
  return (
    <Router basename="/">
      <ScrollToTop />
      <div className='d-flex flex-column min-vh-100'>
        <Routes>
          {/* Public route - Login */}
          <Route 
            path="/login" 
            element={
              !isAuthenticated ? (
                <Login onLoginSuccess={handleLogin} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          
          {/* Root route - redirect based on user type */}
          <Route 
            path="/" 
            element={
              isAuthenticated ? (
                currentEmployee?.userType === '管理者' || currentEmployee?.userType === '現場管理者' || currentEmployee?.userType === '担当現場管理者' ? (
                  <Navigate to="/Admin" replace />
                ) : currentEmployee?.userType === '従業員' ? (
                  <Navigate to="/Employee" replace />
                ) : currentEmployee?.userType === 'construction worker' ? (
                  <Navigate to="/ConstructionWorker" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          
          {/* Admin routes (full admin 管理者 and construction admin 現場管理者) */}
          <Route 
            path="/Admin" 
            element={
              isAuthenticated && (currentEmployee?.userType === '管理者' || currentEmployee?.userType === '現場管理者' || currentEmployee?.userType === '担当現場管理者') ? (
                <HomePage employeeId={currentEmployee.empCode} userType={currentEmployee.userType} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          
          {/* Employee route - for regular employees (従業員) */}
          <Route 
            path="/Employee" 
            element={
              isAuthenticated && currentEmployee?.userType === '従業員' ? (
                <EmployeeView />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          
          {/* Construction Worker route */}
          <Route 
            path="/ConstructionWorker" 
            element={
              isAuthenticated && currentEmployee?.userType === 'construction worker' ? (
                <ConstructionWorkerView />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          
          {/* Legacy User route - redirect to appropriate page */}
          <Route 
            path="/User" 
            element={
              isAuthenticated ? (
                currentEmployee?.userType === '従業員' ? (
                  <Navigate to="/Employee" replace />
                ) : currentEmployee?.userType === 'construction worker' ? (
                  <Navigate to="/ConstructionWorker" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          
          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router>
  );
}


